import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

interface TagInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  className?: string;
  maxTags?: number;
  /** Forwarded to the inner text input so an external <label htmlFor> can associate. */
  id?: string;
  /** Accessible name for the inner text input when there is no visible label. */
  ariaLabel?: string;
}

export function TagInput({
  tags,
  onTagsChange,
  suggestions = [],
  placeholder,
  className,
  maxTags,
  id,
  ariaLabel,
}: TagInputProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Collision boundary for the portaled suggestion list. Inside a dialog the
  // list would otherwise render downward straight over the fixed footer
  // (Cancel/Save); constraining the boundary to the dialog makes Radix flip it
  // upward instead. Resolved after mount (not during render) so SSR and the
  // first paint never touch the DOM; outside a dialog it stays null and Radix
  // falls back to viewport collision detection.
  const [collisionBoundary, setCollisionBoundary] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setCollisionBoundary(
      rootRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null
    );
  }, []);

  const matchesInput = useCallback(
    (suggestion: string, value: string) =>
      !tags.includes(suggestion) &&
      (value === '' || suggestion.toLowerCase().includes(value.toLowerCase())),
    [tags]
  );

  const filteredSuggestions = suggestions.filter(suggestion =>
    matchesInput(suggestion, inputValue)
  );

  const addTag = useCallback((tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      if (!maxTags || tags.length < maxTags) {
        onTagsChange([...tags, trimmedTag]);
      }
    }
    setInputValue('');
    setShowSuggestions(false);
  }, [tags, onTagsChange, maxTags]);

  const removeTag = useCallback((index: number) => {
    onTagsChange(tags.filter((_, i) => i !== index));
  }, [tags, onTagsChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags.length - 1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }, [inputValue, tags, addTag, removeTag]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    // Filter against the INCOMING value, never the render-scoped
    // `filteredSuggestions` — that array belongs to the previous value, so
    // backspacing from a non-matching fragment back to a matching prefix left
    // the list shut until the field was re-focused.
    setShowSuggestions(suggestions.some(s => matchesInput(s, value)));
  }, [suggestions, matchesInput]);

  const open = showSuggestions && filteredSuggestions.length > 0;

  return (
    <Popover open={open} onOpenChange={setShowSuggestions}>
      <div ref={rootRef} className={cn("relative", className)}>
        <PopoverAnchor asChild>
          <div className="min-h-[2.5rem] px-3 py-2 border border-input rounded-md bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <div className="flex flex-wrap gap-1 items-center">
          {/* Keyed by tag, not index: `addTag` rejects duplicates, so the list
              is deduped by construction and the tag itself is the stable id. */}
          {tags.map((tag, index) => (
            <Badge
              key={tag}
              variant="secondary"
              className="text-xs py-1 px-2 gap-1"
            >
              {tag}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                // The Button base forces [&_svg]:size-4, so the box has to be
                // at least h-4/w-4 or it is smaller than its own icon.
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => removeTag(index)}
              >
                <X />
                <span className="sr-only">{t('tagInput.removeTag', { tag })}</span>
              </Button>
            </Badge>
          ))}

          <div className="flex-1 min-w-[120px]">
            <Input
              id={id}
              aria-label={ariaLabel}
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowSuggestions(filteredSuggestions.length > 0)}
              // COMMIT ON BLUR, don't just hide the suggestions.
              //
              // Draft text lives in this component's own state and used to be
              // promoted ONLY by Enter, comma, or the + button. So typing a tag
              // and clicking Save committed everything EXCEPT that tag, behind a
              // success toast — silent data loss, and most likely in the edit
              // dialog, whose whole purpose is "open, add one tag, save".
              //
              // Blur fires before the Save button's click handler, so committing
              // here is what makes the click see the tag. Suggestion clicks never
              // reach this handler at all: the option buttons preventDefault on
              // mousedown, so the input keeps focus and no blur fires. addTag is
              // a no-op on empty/duplicate input, so a plain focus-out is free.
              onBlur={() => addTag(inputValue)}
              placeholder={tags.length === 0 ? (placeholder ?? t('tagInput.placeholder')) : ""}
              className="border-0 p-0 shadow-none focus-visible:ring-0 text-sm"
              disabled={maxTags ? tags.length >= maxTags : false}
            />
          </div>

          {inputValue && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => addTag(inputValue)}
            >
              <Plus />
              <span className="sr-only">{t('tagInput.addTag')}</span>
            </Button>
          )}
          </div>
          </div>
        </PopoverAnchor>

        {/* The suggestion list is PORTALED (Radix Popover), not an inline
            absolute div: inside a dialog the StandardDialogBody scroll
            container (overflow-y-auto) clips any in-flow overlay, which made
            the suggestions invisible for fields near the body's bottom edge.
            The anchor is the whole bordered field; the ANCHOR-width popper var
            sizes the list to match it (--radix-popover-trigger-width is only
            set from a PopoverTrigger, which this component doesn't have). */}
        <PopoverContent
          align="start"
          sideOffset={4}
          collisionBoundary={collisionBoundary ?? undefined}
          className="w-[var(--radix-popper-anchor-width)] p-0 max-h-40 overflow-y-auto"
          // Focus stays in the input while the list is open — the popover is a
          // typeahead surface, not a focus trap. That also means focus is
          // permanently "outside" the content, which Radix would treat as a
          // dismissal the moment the list opens — both outside events must be
          // neutralized for interactions within the field itself.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (rootRef.current?.contains(e.target as Node)) e.preventDefault();
          }}
        >
          {/* Keyed by value: the source list is filtered, never reordered, and
              suggestion values are unique. */}
          {filteredSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
              // mousedown + preventDefault, NOT click: a click would first blur
              // the input, whose handler commits the typed fragment AND closes
              // the (now unmounted) list before the click event ever lands —
              // the suggestion was silently ignored. preventDefault keeps focus
              // in the input, so selecting a suggestion adds exactly that tag.
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(suggestion);
              }}
            >
              {suggestion}
            </button>
          ))}
        </PopoverContent>
      </div>
    </Popover>
  );
}
